import type { ColumnId } from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import type { SpreadsheetId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { apiPost } from '~/_shared/utils/api/api.helpers';
import type { Uuid } from '~/_shared/utils/createUuid';
import type { BoundaryGroupId } from '../boundaries/boundaryIdentifier.type';
import type { ChartMetricType } from '../mapSettings/data/charts/mapSettingsCharts.types';

export enum StatsProcessingStatus {
  InProgress = 'InProgress',
  Finished = 'Finished'
}

type BoundaryStatsRequestItemBase = Readonly<{
  query_id: Uuid;
  boundary_group_id: BoundaryGroupId;
  type: ChartMetricType;
}>;

type BoundaryStatsSpreadsheetColumnRequestItem = BoundaryStatsRequestItemBase & Readonly<{
  type: ChartMetricType.SpreadsheetColumn;
  spreadsheet_id: SpreadsheetId;
  metric: ColumnId;
}>;

type BoundaryStatsDemographicRequestItem = BoundaryStatsRequestItemBase & Readonly<{
  type: ChartMetricType.Demographic;
  metric: string;
}>;

export type BoundaryStatsRequestItem =
  BoundaryStatsSpreadsheetColumnRequestItem
  | BoundaryStatsDemographicRequestItem;

export type BoundaryStatsResponseItem = Readonly<{
  id: number;
  computed_at: number; // epoch timestamp or UTC/ISO datetime with timezone
  boundary_group_id: BoundaryGroupId;
  boundary_group_etag: number;
  type: ChartMetricType;
  metric: ColumnId | string;
  spreadsheet_id?: SpreadsheetId; // when type = 'SpreadsheetColumn'
  spreadsheet_version?: number; // when type = 'SpreadsheetColumn'
  data?: { // when StatsProcessingStatus == 'Finished'
    median: number;
    avg: number;
    values: {
      [boundary_id: string]: number;
    };
  };
  status: StatsProcessingStatus;
}>;

export type BoundaryStatsResponse = Readonly<{
  result: {
    [queryId: Uuid]: BoundaryStatsResponseItem | null;
  };
}>;

export const getBoundaryStats = (
  clientId: number,
  queryItems: ReadonlyArray<BoundaryStatsRequestItem>
): Promise<BoundaryStatsResponse> => {
  const requestUrl = `/api/clients/${clientId}/charts/boundary-statistics`;
  return apiPost(requestUrl, {
    query: queryItems,
  });
};
